import React, { useState, useEffect } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

// User Profile Card
export const UserCard = () => {
    const [user, setUser] = useState(null);
    const [stats, setStats] = useState({ projects: 0, studies: 0, datasets: 0, models: 0 });

    const fetchUserProfile = async () => {
        try {
            const response = await API.get("/accounts/users/me/");
            setUser(response.data);
        } catch (err) {
            console.error("Error fetching user profile:", err);
        }
    };

    const fetchStats = async () => {
        try {
            const [projectsRes, studiesRes, datasetsRes, modelsRes] = await Promise.all([
                API.get('/projects/'),
                API.get('/studies/'),
                API.get('/datasets/'),
                API.get('/ai-models/models/')
            ]);
            setStats({
                projects: projectsRes.data.length,
                studies: studiesRes.data.length,
                datasets: datasetsRes.data.length,
                models: modelsRes.data.length
            });
        } catch (err) {
            console.error("Error fetching stats:", err);
        }
    };

    useEffect(() => {
        fetchUserProfile();
        fetchStats();
    }, []);

    if (!user) {
        return (
            <div className="user-profile-card">
                <div className="profile-header">
                    <div className="spinner-border text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="user-profile-card">
            <div className="profile-header">
                <img
                    src="/assets/logo_no_background.png"
                    alt="Logo"
                    className="profile-logo"
                />
                <div className="profile-info">
                    <h4>{user.first_name} {user.last_name || user.username}</h4>
                    <p>{user.email}</p>
                </div>
            </div>
            <div className="profile-stats">
                <div className="stat-item">
                    <span className="stat-value">{stats.projects}</span>
                    <span className="stat-label">Projects</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{stats.studies}</span>
                    <span className="stat-label">Studies</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{stats.datasets}</span>
                    <span className="stat-label">Datasets</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{stats.models}</span>
                    <span className="stat-label">AI Models</span>
                </div>
            </div>
        </div>
    );
};

// Projects Card
export const ProjectsCard = () => {
    const [projects, setProjects] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await API.get('/projects/');
            setProjects(response.data);
        } catch (err) {
            console.error("Error fetching projects:", err);
            toast.error("Failed to fetch projects.");
        } finally {
            setLoading(false);
        }
    };

    const fetchInvitations = async () => {
        try {
            const response = await API.get('/invitations/');
            setInvitations(response.data);
        } catch (err) {
            console.error("Error fetching invitations:", err);
        }
    };

    useEffect(() => {
        fetchProjects();
        fetchInvitations();
    }, []);

    const handleAcceptInvitation = async (invitationId, e) => {
        e.stopPropagation();
        try {
            await API.post(`/invitations/${invitationId}/accept/`);
            toast.success("Invitation accepted!");
            fetchProjects();
            fetchInvitations();
        } catch (err) {
            console.error("Error accepting invitation:", err);
            toast.error("Failed to accept invitation.");
        }
    };

    const handleDeclineInvitation = async (invitationId, e) => {
        e.stopPropagation();
        try {
            await API.post(`/invitations/${invitationId}/decline/`);
            toast.success("Invitation declined.");
            fetchInvitations();
        } catch (err) {
            console.error("Error declining invitation:", err);
            toast.error("Failed to decline invitation.");
        }
    };

    const handleProjectClick = (projectId) => {
        navigate(`/Projects/${projectId}`);
    };

    const getRoleBadge = (role) => {
        const colors = {
            owner: "bg-primary",
            admin: "bg-warning text-dark",
            editor: "bg-success",
            viewer: "bg-secondary",
        };
        return (
            <span className={`badge ${colors[role] || "bg-secondary"}`}>
                {role}
            </span>
        );
    };

    return (
        <div className="dashboard-section">
            <div className="section-header">
                <img
                    src="/assets/DV_projects2.png"
                    alt="Projects"
                    className="section-icon"
                />
                <div className="section-title-wrapper">
                    <h5 className="section-title">Recent Projects</h5>
                    <p className="section-subtitle">Mission-Ready Collaborative Research Labs</p>
                </div>
                <div className="section-actions">
                    <button
                        className="btn btn-add"
                        onClick={() => navigate('/Projects')}
                    >
                        <i className="fa fa-plus"></i>
                        New Project
                    </button>
                </div>
            </div>
            <div className="section-body p-3">
                {/* Pending Invitations Section */}
                {invitations.length > 0 && (
                    <div className="mb-3">
                        <div className="alert alert-warning mb-3" style={{ borderRadius: '8px' }}>
                            <div className="d-flex align-items-center mb-2">
                                <i className="fa fa-envelope me-2"></i>
                                <strong>Pending Invitations ({invitations.length})</strong>
                            </div>
                            {invitations.map((invitation) => (
                                <div
                                    key={invitation.id}
                                    className="d-flex justify-content-between align-items-center p-2 bg-white rounded mb-2"
                                    style={{ border: '1px solid #ffc107' }}
                                >
                                    <div>
                                        <strong>{invitation.project?.name}</strong>
                                        <span className="text-muted ms-2 small">
                                            from {invitation.invited_by?.username} as{" "}
                                            <span className="badge bg-secondary">{invitation.role}</span>
                                        </span>
                                    </div>
                                    <div>
                                        <button
                                            className="btn btn-sm btn-success me-1"
                                            onClick={(e) => handleAcceptInvitation(invitation.id, e)}
                                            title="Accept invitation"
                                        >
                                            <i className="fa fa-check"></i>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={(e) => handleDeclineInvitation(invitation.id, e)}
                                            title="Decline invitation"
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="empty-state">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : projects.length === 0 && invitations.length === 0 ? (
                    <div className="empty-state">
                        <i className="fa fa-folder-open"></i>
                        <p>No projects yet</p>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate('/Projects')}
                        >
                            Create your first project
                        </button>
                    </div>
                ) : projects.length === 0 ? (
                    <div className="text-center text-muted py-3">
                        <p className="small mb-0">No projects yet. Accept an invitation above or create a new project.</p>
                    </div>
                ) : (
                    <div className="row">
                        {projects.slice(0, 6).map((project) => (
                            <div key={project.id} className="col-md-4 mb-3">
                                <div
                                    className="card h-100 item-card"
                                    onClick={() => handleProjectClick(project.id)}
                                >
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h6 className="card-title mb-0">{project.name}</h6>
                                            {getRoleBadge(project.user_role)}
                                        </div>
                                        <p className="card-text text-muted small">
                                            {project.description || "No description"}
                                        </p>
                                        <div className="d-flex gap-3 text-muted small mt-auto">
                                            <span>
                                                <i className="fa fa-users me-1"></i>
                                                {project.member_count}
                                            </span>
                                            <span>
                                                <i className="fa fa-flask me-1"></i>
                                                {project.study_count}
                                            </span>
                                            <span>
                                                <i className="fa fa-database me-1"></i>
                                                {project.dataset_count}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {projects.length > 6 && (
                    <div className="text-center pt-2">
                        <button
                            className="btn btn-link"
                            onClick={() => navigate('/Projects')}
                        >
                            View all {projects.length} projects
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Studies Card
export const StudiesCard = () => {
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchStudies = async () => {
        try {
            setLoading(true);
            const response = await API.get('/studies/');
            setStudies(response.data);
        } catch (err) {
            console.error("Error fetching studies:", err);
            toast.error("Failed to fetch studies.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStudies();
    }, []);

    const handleStudyClick = (studyId) => {
        navigate(`/Study/${studyId}/`);
    };

    const isReviewerStudy = (study) => study?.user_assignment?.role === 'reviewer';
    const isReviewComplete = (study) => {
        const p = study?.review_progress;
        return !!p && p.assigned > 0 && p.completed >= p.assigned;
    };
    const isReviewPending = (study) => isReviewerStudy(study) && !isReviewComplete(study);

    const getStatusBadge = (status) => {
        const colors = {
            draft: "bg-secondary",
            active: "bg-primary",
            completed: "bg-success",
            archived: "bg-dark",
        };
        return (
            <span className={`badge ${colors[status] || "bg-secondary"}`}>
                {status || 'draft'}
            </span>
        );
    };

    // Pending reviews first, then completed reviews, then everything else.
    const rank = (s) => (isReviewPending(s) ? 0 : isReviewerStudy(s) ? 1 : 2);
    const sortedStudies = [...studies].sort((a, b) => rank(a) - rank(b));

    const pendingReviewStudies = studies.filter(isReviewPending);
    const completedReviewStudies = studies.filter(
        (s) => isReviewerStudy(s) && isReviewComplete(s)
    );

    return (
        <div className="dashboard-section">
            <div className="section-header">
                <img
                    src="/assets/DV_studies3.png"
                    alt="Studies"
                    className="section-icon"
                />
                <div className="section-title-wrapper">
                    <h5 className="section-title">Recent Studies</h5>
                    <p className="section-subtitle">Medical Data Observatory & AI Model Validation Center</p>
                </div>
                <div className="section-actions">
                    <button
                        className="btn btn-add"
                        onClick={() => navigate('/Studies')}
                    >
                        <i className="fa fa-plus"></i>
                        New Study
                    </button>
                </div>
            </div>
            <div className="section-body p-3">
                {pendingReviewStudies.length > 0 && (
                    <div
                        className="alert mb-3 d-flex align-items-center justify-content-between"
                        style={{
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #fff8e1 0%, #ffe0b2 100%)',
                            border: '1px solid #ffb300',
                            color: '#5d4037',
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <i
                                className="fa fa-stethoscope me-3"
                                style={{ fontSize: '24px', color: '#e65100' }}
                            ></i>
                            <div>
                                <strong>
                                    {pendingReviewStudies.length === 1
                                        ? 'You have 1 study to review'
                                        : `You have ${pendingReviewStudies.length} studies to review`}
                                </strong>
                                <div className="small">
                                    Click a highlighted study below to start reviewing.
                                </div>
                            </div>
                        </div>
                        {pendingReviewStudies.length === 1 && (
                            <button
                                className="btn btn-warning btn-sm fw-semibold"
                                onClick={() => handleStudyClick(pendingReviewStudies[0].id)}
                            >
                                Open study <i className="fa fa-arrow-right ms-1"></i>
                            </button>
                        )}
                    </div>
                )}
                {pendingReviewStudies.length === 0 && completedReviewStudies.length > 0 && (
                    <div
                        className="alert mb-3 d-flex align-items-center"
                        style={{
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                            border: '1px solid #2e7d32',
                            color: '#1b5e20',
                        }}
                    >
                        <i
                            className="fa fa-check-circle me-3"
                            style={{ fontSize: '24px', color: '#2e7d32' }}
                        ></i>
                        <div>
                            <strong>
                                {completedReviewStudies.length === 1
                                    ? 'All your assigned cases are reviewed'
                                    : `All cases reviewed across ${completedReviewStudies.length} studies`}
                            </strong>
                            <div className="small">
                                Nothing pending — thanks for your work.
                            </div>
                        </div>
                    </div>
                )}
                {loading ? (
                    <div className="empty-state">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : studies.length === 0 ? (
                    <div className="empty-state">
                        <i className="fa fa-book"></i>
                        <p>No studies yet</p>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate('/Studies')}
                        >
                            Create your first study
                        </button>
                    </div>
                ) : (
                    <div className="row">
                        {sortedStudies.slice(0, 6).map((study) => {
                            const reviewer = isReviewerStudy(study);
                            const done = reviewer && isReviewComplete(study);
                            const pending = reviewer && !done;
                            const progress = study.review_progress;
                            let cardStyle;
                            if (pending) {
                                cardStyle = {
                                    border: '2px solid #ffb300',
                                    boxShadow: '0 0 0 4px rgba(255, 179, 0, 0.15)',
                                };
                            } else if (done) {
                                cardStyle = {
                                    border: '2px solid #2e7d32',
                                    boxShadow: '0 0 0 4px rgba(46, 125, 50, 0.12)',
                                };
                            }
                            return (
                                <div key={study.id} className="col-md-4 mb-3">
                                    <div
                                        className={`card h-100 item-card${reviewer ? ' study-review-card' : ''}`}
                                        onClick={() => handleStudyClick(study.id)}
                                        style={cardStyle}
                                    >
                                        {pending && (
                                            <div
                                                className="d-flex align-items-center justify-content-between px-3 py-2"
                                                style={{
                                                    background: 'linear-gradient(135deg, #ffb300 0%, #ff8f00 100%)',
                                                    color: '#fff',
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    borderTopLeftRadius: '6px',
                                                    borderTopRightRadius: '6px',
                                                }}
                                            >
                                                <span>
                                                    <i className="fa fa-stethoscope me-2"></i>
                                                    Review needed
                                                    {progress && progress.assigned > 0 && (
                                                        <span className="ms-2 fw-normal" style={{ opacity: 0.9 }}>
                                                            {progress.completed}/{progress.assigned}
                                                        </span>
                                                    )}
                                                </span>
                                                <i className="fa fa-arrow-right"></i>
                                            </div>
                                        )}
                                        {done && (
                                            <div
                                                className="d-flex align-items-center justify-content-between px-3 py-2"
                                                style={{
                                                    background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
                                                    color: '#fff',
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    borderTopLeftRadius: '6px',
                                                    borderTopRightRadius: '6px',
                                                }}
                                            >
                                                <span>
                                                    <i className="fa fa-check-circle me-2"></i>
                                                    Review complete
                                                    {progress && progress.assigned > 0 && (
                                                        <span className="ms-2 fw-normal" style={{ opacity: 0.9 }}>
                                                            {progress.completed}/{progress.assigned}
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="card-title mb-0">{study.name}</h6>
                                                {getStatusBadge(study.status)}
                                            </div>
                                            <p className="card-text text-muted small">
                                                {study.description || "No description"}
                                            </p>
                                            <div className="d-flex gap-3 text-muted small mt-auto">
                                                <span>
                                                    <i className="fa fa-database me-1"></i>
                                                    {study.dataset_count || 0} datasets
                                                </span>
                                                {study.project_name && (
                                                    <span>
                                                        <i className="fa fa-folder me-1"></i>
                                                        {study.project_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="card-footer bg-transparent">
                                            <small className="text-muted">
                                                {new Date(study.created_at).toLocaleDateString()}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {studies.length > 6 && (
                    <div className="text-center pt-2">
                        <button
                            className="btn btn-link"
                            onClick={() => navigate('/Studies')}
                        >
                            View all {studies.length} studies
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Models Card
export const ModelsCard = () => {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchModels = async () => {
        try {
            setLoading(true);
            // Models only — tools have their own card and mixing them would
            // show wrenches under "Recent Models".
            const response = await API.get('/ai-models/models/', { params: { entity_type: 'model' } });
            setModels(response.data);
        } catch (err) {
            console.error("Error fetching models:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModels();
    }, []);

    const getBuildStatusBadge = (status) => {
        const colors = {
            ready: "bg-success",
            building: "bg-warning text-dark",
            pending: "bg-secondary",
            failed: "bg-danger",
        };
        return (
            <span className={`badge ${colors[status] || "bg-secondary"}`}>
                {status || 'pending'}
            </span>
        );
    };

    const handleModelClick = (modelId) => {
        navigate(`/Models`);
    };

    return (
        <div className="dashboard-section">
            <div className="section-header">
                <img
                    src="/assets/DV_models2.png"
                    alt="Models"
                    className="section-icon"
                />
                <div className="section-title-wrapper">
                    <h5 className="section-title">Recent Models</h5>
                    <p className="section-subtitle">Medical AI Command Center for Analysis and Inference</p>
                </div>
                <div className="section-actions">
                    <button
                        className="btn btn-add"
                        onClick={() => navigate('/Models/New')}
                    >
                        <i className="fa fa-plus"></i>
                        Add Model
                    </button>
                </div>
            </div>
            <div className="section-body p-3">
                {loading ? (
                    <div className="empty-state">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : models.length === 0 ? (
                    <div className="empty-state">
                        <i className="fa fa-cube"></i>
                        <p>No AI models yet</p>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate('/Models/New')}
                        >
                            Import your first model
                        </button>
                    </div>
                ) : (
                    <div className="row">
                        {models.slice(0, 6).map((model) => (
                            <div key={model.id} className="col-md-4 mb-3">
                                <div
                                    className="card h-100 item-card"
                                    onClick={() => handleModelClick(model.id)}
                                >
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="d-flex align-items-center">
                                                <i
                                                    className={`fa ${model.icon || 'fa-cube'} me-2`}
                                                    style={{ color: model.color || '#6f42c1' }}
                                                ></i>
                                                <h6 className="card-title mb-0">{model.name}</h6>
                                            </div>
                                            {getBuildStatusBadge(model.build_status)}
                                        </div>
                                        <p className="card-text text-muted small">
                                            {model.description || "No description"}
                                        </p>
                                        <div className="d-flex gap-3 text-muted small mt-auto">
                                            <span>
                                                <i className="fa fa-tag me-1"></i>
                                                {model.model_type_display || model.model_type}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-transparent">
                                        <small className="text-muted">
                                            {new Date(model.created_at).toLocaleDateString()}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {models.length > 6 && (
                    <div className="text-center pt-2">
                        <button
                            className="btn btn-link"
                            onClick={() => navigate('/Models')}
                        >
                            View all {models.length} models
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Datasets Card
export const DatasetsCard = () => {
    const [datasets, setDatasets] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchDatasets = async () => {
        try {
            setLoading(true);
            const response = await API.get('/datasets/');
            setDatasets(response.data);
        } catch (err) {
            console.error("Error fetching datasets:", err);
            toast.error("Failed to fetch datasets.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDatasets();
    }, []);

    const handleDatasetClick = (datasetId) => {
        navigate(`/Datasets/${datasetId}/`);
    };

    return (
        <div className="dashboard-section">
            <div className="section-header">
                <img
                    src="/assets/DV_datasets2.png"
                    alt="Datasets"
                    className="section-icon"
                />
                <div className="section-title-wrapper">
                    <h5 className="section-title">Recent Datasets</h5>
                    <p className="section-subtitle">Health Intelligence Datasets for Scientific Expeditions</p>
                </div>
                <div className="section-actions">
                    <button
                        className="btn btn-add"
                        onClick={() => navigate('/CreateDataset')}
                    >
                        <i className="fa fa-plus"></i>
                        Add Dataset
                    </button>
                </div>
            </div>
            <div className="section-body p-3">
                {loading ? (
                    <div className="empty-state">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : datasets.length === 0 ? (
                    <div className="empty-state">
                        <i className="fa fa-database"></i>
                        <p>No datasets yet</p>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate('/CreateDataset')}
                        >
                            Create your first dataset
                        </button>
                    </div>
                ) : (
                    <div className="row">
                        {datasets.slice(0, 6).map((dataset) => (
                            <div key={dataset.id} className="col-md-4 mb-3">
                                <div
                                    className="card h-100 item-card"
                                    onClick={() => handleDatasetClick(dataset.id)}
                                >
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h6 className="card-title mb-0">{dataset.name}</h6>
                                            <span className="badge bg-info">
                                                {dataset.image_count || 0} images
                                            </span>
                                        </div>
                                        <p className="card-text text-muted small">
                                            {dataset.description || "No description"}
                                        </p>
                                        <div className="d-flex gap-3 text-muted small mt-auto">
                                            {dataset.project_name && (
                                                <span>
                                                    <i className="fa fa-folder me-1"></i>
                                                    {dataset.project_name}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="card-footer bg-transparent">
                                        <small className="text-muted">
                                            {new Date(dataset.created_at).toLocaleDateString()}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {datasets.length > 6 && (
                    <div className="text-center pt-2">
                        <button
                            className="btn btn-link"
                            onClick={() => navigate('/Datasets')}
                        >
                            View all {datasets.length} datasets
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Viewers Card
export const ViewersCard = () => {
    const [viewers, setViewers] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchViewers = async () => {
        try {
            setLoading(true);
            const response = await API.get('/viewers/');
            setViewers(response.data);
        } catch (err) {
            console.error("Error fetching viewers:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchViewers();
    }, []);

    return (
        <div className="dashboard-section">
            <div className="section-header">
                <div
                    className="section-icon d-flex align-items-center justify-content-center"
                    style={{ background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)', color: '#fff' }}
                >
                    <i className="fa fa-desktop" style={{ fontSize: '28px' }}></i>
                </div>
                <div className="section-title-wrapper">
                    <h5 className="section-title">Recent Viewers</h5>
                    <p className="section-subtitle">Configured Viewers for Data Visualization Missions</p>
                </div>
                <div className="section-actions">
                    <button
                        className="btn btn-add"
                        onClick={() => navigate('/ViewerCreationLab')}
                    >
                        <i className="fa fa-plus"></i>
                        New Viewer
                    </button>
                </div>
            </div>
            <div className="section-body p-3">
                {loading ? (
                    <div className="empty-state">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : viewers.length === 0 ? (
                    <div className="empty-state">
                        <i className="fa fa-desktop"></i>
                        <p>No viewers yet</p>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate('/ViewerCreationLab')}
                        >
                            Create your first viewer
                        </button>
                    </div>
                ) : (
                    <div className="row">
                        {viewers.slice(0, 6).map((viewer) => (
                            <div key={viewer.id} className="col-md-4 mb-3">
                                <div
                                    className="card h-100 item-card"
                                    onClick={() => navigate('/Viewers')}
                                >
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="d-flex align-items-center">
                                                <i
                                                    className={`fa ${viewer.icon || 'fa-eye'} me-2`}
                                                    style={{ color: viewer.color || '#007bff' }}
                                                ></i>
                                                <h6 className="card-title mb-0">{viewer.name}</h6>
                                            </div>
                                            {viewer.is_active ? (
                                                <span className="badge bg-success">Active</span>
                                            ) : (
                                                <span className="badge bg-secondary">Inactive</span>
                                            )}
                                        </div>
                                        <p className="card-text text-muted small">
                                            {viewer.description || "No description"}
                                        </p>
                                        <div className="d-flex gap-3 text-muted small mt-auto">
                                            <span>
                                                <i className="fa fa-tag me-1"></i>
                                                {viewer.viewer_type_display || viewer.viewer_type}
                                            </span>
                                            <span>
                                                <i className="fa fa-flask me-1"></i>
                                                {viewer.study_count || 0} studies
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-transparent">
                                        <small className="text-muted">
                                            {new Date(viewer.created_at).toLocaleDateString()}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {viewers.length > 6 && (
                    <div className="text-center pt-2">
                        <button
                            className="btn btn-link"
                            onClick={() => navigate('/Viewers')}
                        >
                            View all {viewers.length} viewers
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Tools Card
export const ToolsCard = () => {
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchTools = async () => {
        try {
            setLoading(true);
            const response = await API.get('/ai-models/models/', { params: { entity_type: 'tool' } });
            setTools(response.data);
        } catch (err) {
            console.error("Error fetching tools:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTools();
    }, []);

    const getBuildStatusBadge = (status) => {
        const colors = {
            ready: "bg-success",
            building: "bg-warning text-dark",
            pending: "bg-secondary",
            failed: "bg-danger",
        };
        return (
            <span className={`badge ${colors[status] || "bg-secondary"}`}>
                {status || 'pending'}
            </span>
        );
    };

    return (
        <div className="dashboard-section">
            <div className="section-header">
                <div
                    className="section-icon d-flex align-items-center justify-content-center"
                    style={{ background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)', color: '#fff' }}
                >
                    <i className="fa fa-wrench" style={{ fontSize: '28px' }}></i>
                </div>
                <div className="section-title-wrapper">
                    <h5 className="section-title">Recent Tools</h5>
                    <p className="section-subtitle">Processing Tools for Signal, Data & Custom Workflows</p>
                </div>
                <div className="section-actions">
                    <button
                        className="btn btn-add"
                        onClick={() => navigate('/Models/NewTool')}
                    >
                        <i className="fa fa-plus"></i>
                        Add Tool
                    </button>
                </div>
            </div>
            <div className="section-body p-3">
                {loading ? (
                    <div className="empty-state">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : tools.length === 0 ? (
                    <div className="empty-state">
                        <i className="fa fa-wrench"></i>
                        <p>No tools yet</p>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate('/Models/NewTool')}
                        >
                            Add your first tool
                        </button>
                    </div>
                ) : (
                    <div className="row">
                        {tools.slice(0, 6).map((tool) => (
                            <div key={tool.id} className="col-md-4 mb-3">
                                <div
                                    className="card h-100 item-card"
                                    onClick={() => navigate('/Tools')}
                                >
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="d-flex align-items-center">
                                                <i
                                                    className={`fa ${tool.icon || 'fa-wrench'} me-2`}
                                                    style={{ color: tool.color || '#6c757d' }}
                                                ></i>
                                                <h6 className="card-title mb-0">{tool.name}</h6>
                                            </div>
                                            {getBuildStatusBadge(tool.build_status)}
                                        </div>
                                        <p className="card-text text-muted small">
                                            {tool.description || "No description"}
                                        </p>
                                        <div className="d-flex gap-3 text-muted small mt-auto">
                                            <span>
                                                <i className="fa fa-tag me-1"></i>
                                                {tool.model_type_display || tool.model_type}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-transparent">
                                        <small className="text-muted">
                                            {new Date(tool.created_at).toLocaleDateString()}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {tools.length > 6 && (
                    <div className="text-center pt-2">
                        <button
                            className="btn btn-link"
                            onClick={() => navigate('/Tools')}
                        >
                            View all {tools.length} tools
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserCard;
